import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Shield, RefreshCw, CheckCircle, XCircle, AlertTriangle, Play, Square, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeploymentDashboard() {
  const { user } = useAuth();
  const [audit, setAudit] = useState(null);
  const [smoke, setSmoke] = useState(null);
  const [gateSummary, setGateSummary] = useState(null);
  const [batches, setBatches] = useState([]);
  const [observations, setObservations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showObsDialog, setShowObsDialog] = useState(false);
  const [obsForm, setObsForm] = useState({ type: 'issue', severity: 'low', affected_role: 'system', description: '', suggested_fix: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [auditRes, smokeRes, gateRes, batchRes, obsRes] = await Promise.all([
        axios.get(`${API}/api/deployment/audit`, { headers }),
        axios.get(`${API}/api/deployment/smoke-test`, { headers }),
        axios.get(`${API}/api/deployment/feature-gate/summary`, { headers }),
        axios.get(`${API}/api/deployment/rollout/batches`, { headers }),
        axios.get(`${API}/api/deployment/observation/log`, { headers }),
      ]);
      setAudit(auditRes.data);
      setSmoke(smokeRes.data);
      setGateSummary(gateRes.data);
      setBatches(batchRes.data.batches || []);
      setObservations(obsRes.data);
    } catch (e) { toast.error('Failed to load deployment data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const emergencyRollback = async () => {
    if (!window.confirm('EMERGENCY ROLLBACK: This will disable the new journey model for ALL subscribers. Continue?')) return;
    try {
      const res = await axios.post(`${API}/api/deployment/rollback/disable-all`, {}, { headers });
      toast.success(`Rollback complete: ${res.data.subscribers_reverted} reverted`);
      loadData();
    } catch { toast.error('Rollback failed'); }
  };

  const logObservation = async () => {
    try {
      await axios.post(`${API}/api/deployment/observation/log`, obsForm, { headers });
      toast.success('Observation logged');
      setShowObsDialog(false);
      setObsForm({ type: 'issue', severity: 'low', affected_role: 'system', description: '', suggested_fix: '' });
      loadData();
    } catch { toast.error('Failed to log observation'); }
  };

  const StatusIcon = ({ status }) => {
    if (status === 'PASS') return <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />;
    if (status === 'FAIL') return <XCircle className="w-3.5 h-3.5 text-red-400" />;
    return <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />;
  };

  return (
    <AdminLayout>
      <div data-testid="deployment-dashboard" className="p-6 max-w-7xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Production Deployment</h1>
            <p className="text-sm text-white/40 mt-0.5">Zero-Risk Controlled Rollout</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="deployment-refresh" variant="outline" size="sm" onClick={loadData} disabled={loading}
              className="border-white/10 text-white/60">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button data-testid="deployment-emergency-rollback" variant="outline" size="sm" onClick={emergencyRollback}
              className="border-red-700/50 text-red-400 hover:bg-red-900/30">
              Emergency Rollback
            </Button>
          </div>
        </div>

        {/* Audit + Smoke Test */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-[#0d1117] border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white/80 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Pre-Deployment Audit
                {audit && <Badge className={audit.overall === 'PASS' ? 'bg-emerald-800 text-emerald-200' : 'bg-red-800 text-red-200'}>{audit.overall}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {audit && Object.entries(audit.checks || {}).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <StatusIcon status={val.status} />
                    <span className="text-white/60">{key.replace(/_/g, ' ')}</span>
                  </div>
                  <span className="text-white/30 text-[10px]">{val.status}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-[#0d1117] border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white/80 flex items-center gap-2">
                <Play className="w-4 h-4" /> Smoke Test
                {smoke && <Badge className={smoke.overall === 'PASS' ? 'bg-emerald-800 text-emerald-200' : 'bg-red-800 text-red-200'}>{smoke.overall}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {smoke && (smoke.tests || []).map((t, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <StatusIcon status={t.status} />
                    <span className="text-white/60">{t.name.replace(/_/g, ' ')}</span>
                  </div>
                  <span className="text-white/30 text-[10px]">{t.detail?.slice(0, 30)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Feature Gate Summary */}
        <Card className="bg-[#0d1117] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-white">Feature Gate Status</p>
              <Badge className="bg-white/5 text-white/40 text-[10px]">is_new_journey_model_active</Badge>
            </div>
            {gateSummary && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-black/20 rounded p-3 text-center">
                  <p className="text-xl font-bold text-white">{gateSummary.total_subscribers}</p>
                  <p className="text-[10px] text-white/30">Total Subscribers</p>
                </div>
                <div className="bg-emerald-900/20 rounded p-3 text-center border border-emerald-800/30">
                  <p className="text-xl font-bold text-emerald-400">{gateSummary.new_model_enabled}</p>
                  <p className="text-[10px] text-white/30">New Model Enabled</p>
                </div>
                <div className="bg-slate-800/40 rounded p-3 text-center">
                  <p className="text-xl font-bold text-white/60">{gateSummary.legacy_model}</p>
                  <p className="text-[10px] text-white/30">Legacy Model</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rollout Batches */}
        <Card className="bg-[#0d1117] border-white/5">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-white mb-3">Rollout Batches</p>
            {batches.length === 0 ? (
              <p className="text-xs text-white/30">No rollout batches created yet</p>
            ) : (
              <div className="space-y-2">
                {batches.map(b => (
                  <div key={b.batch_id} className="flex items-center justify-between bg-black/20 rounded p-3">
                    <div>
                      <p className="text-xs font-medium text-white">{b.name}</p>
                      <p className="text-[10px] text-white/30">{b.batch_id} - {b.subscriber_ids?.length} subscribers</p>
                    </div>
                    <Badge className={
                      b.status === 'active' ? 'bg-emerald-800 text-emerald-200' :
                      b.status === 'deactivated' ? 'bg-slate-700 text-slate-300' :
                      b.status === 'emergency_deactivated' ? 'bg-red-800 text-red-200' :
                      'bg-blue-800 text-blue-200'
                    }>{b.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Observation Log */}
        <Card className="bg-[#0d1117] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-white">Production Observation Log</p>
              <Button data-testid="deployment-log-observation" size="sm" variant="outline"
                className="border-white/10 text-white/60 text-xs"
                onClick={() => setShowObsDialog(true)}>
                <FileText className="w-3 h-3 mr-1" /> Log Observation
              </Button>
            </div>
            {observations && (
              <div className="flex gap-3 mb-3">
                <Badge className="bg-white/5 text-white/50 text-[10px]">Total: {observations.stats.total}</Badge>
                {observations.stats.critical > 0 && <Badge className="bg-red-800 text-red-200 text-[10px]">Critical: {observations.stats.critical}</Badge>}
                {observations.stats.unresolved > 0 && <Badge className="bg-amber-800 text-amber-200 text-[10px]">Unresolved: {observations.stats.unresolved}</Badge>}
              </div>
            )}
            {observations?.observations?.length > 0 ? (
              <div className="space-y-1.5">
                {observations.observations.slice(0, 10).map(o => (
                  <div key={o.observation_id} className="flex items-center justify-between bg-black/20 rounded p-2">
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[9px] ${
                        o.severity === 'critical' ? 'bg-red-800 text-red-200' :
                        o.severity === 'high' ? 'bg-amber-800 text-amber-200' :
                        'bg-white/5 text-white/40'
                      }`}>{o.severity}</Badge>
                      <span className="text-[11px] text-white/60">{o.description?.slice(0, 80)}</span>
                    </div>
                    <Badge className={o.resolved ? 'bg-emerald-800/30 text-emerald-300' : 'bg-amber-800/30 text-amber-300'} variant="outline">
                      {o.resolved ? 'resolved' : 'open'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-white/30">No observations logged</p>
            )}
          </CardContent>
        </Card>

        {/* Log Observation Dialog */}
        <Dialog open={showObsDialog} onOpenChange={setShowObsDialog}>
          <DialogContent className="bg-[#1a1a2e] border-white/10 text-white">
            <DialogHeader><DialogTitle>Log Production Observation</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <p className="text-[10px] text-white/40 mb-1">Type</p>
                  <Select value={obsForm.type} onValueChange={v => setObsForm(p => ({ ...p, type: v }))}>
                    <SelectTrigger className="bg-black/30 border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-white/10">
                      <SelectItem value="issue" className="text-white">Issue</SelectItem>
                      <SelectItem value="insight" className="text-white">Insight</SelectItem>
                      <SelectItem value="note" className="text-white">Note</SelectItem>
                      <SelectItem value="anomaly" className="text-white">Anomaly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-[10px] text-white/40 mb-1">Severity</p>
                  <Select value={obsForm.severity} onValueChange={v => setObsForm(p => ({ ...p, severity: v }))}>
                    <SelectTrigger className="bg-black/30 border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-white/10">
                      <SelectItem value="critical" className="text-white">Critical</SelectItem>
                      <SelectItem value="high" className="text-white">High</SelectItem>
                      <SelectItem value="medium" className="text-white">Medium</SelectItem>
                      <SelectItem value="low" className="text-white">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-[10px] text-white/40 mb-1">Affected Role</p>
                  <Select value={obsForm.affected_role} onValueChange={v => setObsForm(p => ({ ...p, affected_role: v }))}>
                    <SelectTrigger className="bg-black/30 border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-white/10">
                      <SelectItem value="admin" className="text-white">Admin</SelectItem>
                      <SelectItem value="counselor" className="text-white">Counselor</SelectItem>
                      <SelectItem value="health_coach" className="text-white">Health Coach</SelectItem>
                      <SelectItem value="subscriber" className="text-white">Subscriber</SelectItem>
                      <SelectItem value="system" className="text-white">System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <p className="text-[10px] text-white/40 mb-1">Description</p>
                <Textarea className="bg-black/30 border-white/10 text-white text-xs min-h-[60px]"
                  value={obsForm.description} onChange={e => setObsForm(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div>
                <p className="text-[10px] text-white/40 mb-1">Suggested Fix</p>
                <Input className="bg-black/30 border-white/10 text-white text-xs"
                  value={obsForm.suggested_fix} onChange={e => setObsForm(p => ({ ...p, suggested_fix: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowObsDialog(false)} className="text-white/60">Cancel</Button>
              <Button data-testid="deployment-save-observation" onClick={logObservation}
                disabled={!obsForm.description} className="bg-blue-600 hover:bg-blue-700">Log</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
